import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { QRCodeSVG } from 'qrcode.react';
import { motion } from 'framer-motion';
import { useSocket } from '../context/SocketContext';
import { C, pageVariants, shared } from '../theme/space';
import { IconWifi, IconBot, IconZap } from '../components/Icons';

const WA_CONFIG = {
  ready:         { border: 'rgba(52,211,153,0.4)',  bg: 'rgba(52,211,153,0.06)',  color: '#34d399', label: 'CONNECTED',      text: 'Bot is live and auto-replying to incoming messages.' },
  authenticated: { border: 'rgba(251,191,36,0.4)',  bg: 'rgba(251,191,36,0.06)',  color: '#fbbf24', label: 'AUTHENTICATING', text: 'Session confirmed — finalising connection...' },
  qr_ready:      { border: 'rgba(251,146,60,0.4)',  bg: 'rgba(251,146,60,0.06)',  color: '#fb923c', label: 'SCAN QR CODE',   text: 'Scan the QR below with your WhatsApp to go live.' },
  pairing_code:  { border: 'rgba(129,140,248,0.4)', bg: 'rgba(129,140,248,0.06)', color: '#818cf8', label: 'PAIRING CODE',   text: 'Enter the code below in your WhatsApp Linked Devices.' },
  disconnected:  { border: 'rgba(248,113,113,0.4)', bg: 'rgba(248,113,113,0.06)', color: '#f87171', label: 'DISCONNECTED',   text: 'Your WhatsApp client is offline. Connect to start broadcasting.' },
};

export default function WhatsApp() {
  const { qrCode, waStatus, pairingCode } = useSocket();
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [showPairing, setShowPairing] = useState(false);
  const [error, setError] = useState('');
  const [botActive, setBotActive] = useState(true);
  const [toggling, setToggling] = useState(false);
  const pairingPhoneRef = useRef(null);

  const waCfg = WA_CONFIG[waStatus] || WA_CONFIG.disconnected;

  // Initial fetch of bot state
  useEffect(() => {
    api.get('/api/whatsapp/global-bot')
      .then(res => setBotActive(res.data.active))
      .catch(console.error);
  }, []);

  const toggleBot = async () => {
    setToggling(true);
    try {
      const res = await api.post('/api/whatsapp/global-bot', { active: !botActive });
      setBotActive(res.data.active);
    } catch (err) {
      setError('Failed to toggle bot status');
    } finally {
      setToggling(false);
    }
  };

  // When pairing mode: auto-request code as soon as client is qr_ready
  useEffect(() => {
    if (waStatus === 'qr_ready' && pairingPhoneRef.current) {
      const pendingPhone = pairingPhoneRef.current;
      pairingPhoneRef.current = null;
      api.post('/api/whatsapp/request-pairing-code', { phone: pendingPhone })
        .catch(err => setError(err.response?.data?.error || 'Failed to request pairing code'));
    }
  }, [waStatus]);

  const handleInit = async (usePairing = false) => {
    setLoading(true);
    setError('');
    try {
      if (usePairing && !phone) {
        setError('Please enter a phone number for pairing');
        setLoading(false);
        return;
      }

      if (usePairing) {
        pairingPhoneRef.current = phone;
        await api.post('/api/whatsapp/init-pairing');
      } else {
        await api.post('/api/whatsapp/init');
      }
    } catch (err) {
      pairingPhoneRef.current = null;
      setError(err.response?.data?.error || 'Failed to initialize WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to disconnect WhatsApp?')) return;
    setLoading(true);
    try {
      await api.post('/api/whatsapp/logout');
    } catch (err) {
      setError('Failed to logout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div style={s.page} variants={pageVariants} initial="hidden" animate="show">
      <div style={s.header}>
        <div>
          <h1 style={shared.title}>WhatsApp & Bot</h1>
          <p style={s.subtitle}>Manage your WhatsApp connection and automation settings</p>
        </div>
      </div>

      <div style={s.grid}>
        {/* Connection Status Card */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <div style={{ ...s.iconCircle, background: waCfg.bg }}>
              <IconWifi color={waCfg.color} size={20} />
            </div>
            <div>
              <h3 style={s.cardTitle}>Connection Status</h3>
              <span style={{ color: waCfg.color, fontSize: 12, fontWeight: 700 }}>{waCfg.label}</span>
            </div>
          </div>
          
          <p style={s.cardText}>{waCfg.text}</p>

          {waStatus === 'disconnected' && (
            <div style={s.actionBox}>
              {!showPairing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <button 
                    style={s.primaryBtn} 
                    onClick={() => handleInit(false)}
                    disabled={loading}
                  >
                    {loading ? 'Starting...' : 'Connect via QR Code'}
                  </button>
                  <button 
                    style={s.secondaryBtn} 
                    onClick={() => setShowPairing(true)}
                    disabled={loading}
                  >
                    Connect via Phone Number
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <input 
                    style={s.input}
                    placeholder="Phone number (e.g. 919876543210)"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={s.secondaryBtn} onClick={() => setShowPairing(false)}>Back</button>
                    <button 
                      style={s.primaryBtn} 
                      onClick={() => handleInit(true)}
                      disabled={loading}
                    >
                      {loading ? 'Starting...' : 'Get Code'}
                    </button>
                  </div>
                </div>
              )}
              {error && <p style={s.error}>{error}</p>}
            </div>
          )}

          {waStatus !== 'disconnected' && (
            <button style={s.logoutBtn} onClick={handleLogout} disabled={loading}>
              Disconnect Account
            </button>
          )}
        </div>

        {/* QR / Pairing Code Card */}
        {(waStatus === 'qr_ready' || waStatus === 'pairing_code') && (
          <motion.div 
            style={s.card}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h3 style={s.cardTitle}>{waStatus === 'qr_ready' ? 'Scan QR Code' : 'Pairing Code'}</h3>
            <p style={s.cardText}>
              {waStatus === 'qr_ready' 
                ? 'Open WhatsApp on your phone, go to Linked Devices, and scan this code.'
                : 'Open WhatsApp on your phone, go to Linked Devices > Link with phone number, and enter this code.'}
            </p>
            
            <div style={s.codeContainer}>
              {waStatus === 'qr_ready' && qrCode ? (
                <div style={s.qrWrapper}>
                  <QRCodeSVG value={qrCode} size={200} />
                </div>
              ) : waStatus === 'pairing_code' && pairingCode ? (
                <div style={s.pairingCode}>
                  {pairingCode.split('').map((char, i) => (
                    <span key={i} style={s.codeChar}>{char}</span>
                  ))}
                </div>
              ) : (
                <div style={s.loader} />
              )}
            </div>
          </motion.div>
        )}

        {/* Bot Settings Card */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <div style={{ ...s.iconCircle, background: 'rgba(99,102,241,0.1)' }}>
              <IconBot color={C.primary} size={20} />
            </div>
            <div>
              <h3 style={s.cardTitle}>Auto-Reply Bot</h3>
              <span style={{ color: C.textMuted, fontSize: 12 }}>Global Settings</span>
            </div>
          </div>
          <p style={s.cardText}>When enabled, the AI bot will automatically handle incoming messages using your knowledge base.</p>
          <div style={s.toggleRow}>
             <span style={{ fontSize: 14, color: C.text, fontWeight: 500 }}>AI Auto-Reply</span>
             <label style={s.toggle}>
                <input 
                  type="checkbox" 
                  style={s.toggleInput} 
                  checked={botActive} 
                  onChange={toggleBot}
                  disabled={toggling}
                />
                <span style={{ ...s.toggleDot, transform: botActive ? 'translateX(20px)' : 'translateX(0px)', background: botActive ? C.primary : '#94a3b8' }} />
             </label>
          </div>
          <div style={{ fontSize: 11, color: botActive ? C.success : '#94a3b8', fontWeight: 700, textAlign: 'right', marginTop: -8 }}>
            {botActive ? 'ACTIVE' : 'INACTIVE'}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

const s = {
  page: { padding: '28px 32px', flex: 1, overflowY: 'auto' },
  header: { marginBottom: 32 },
  subtitle: { color: C.textMuted, fontSize: 14, marginTop: 4 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24 },
  card: {
    background: 'rgba(6, 12, 28, 0.88)',
    borderRadius: 16,
    padding: 24,
    border: '1px solid rgba(99,102,241,0.12)',
    backdropFilter: 'blur(12px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: 16 },
  iconCircle: { width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardTitle: { fontSize: 16, fontWeight: 600, color: C.text, margin: 0 },
  cardText: { fontSize: 14, color: C.textMuted, lineHeight: 1.5, margin: 0 },
  actionBox: { marginTop: 8 },
  primaryBtn: { 
    background: 'linear-gradient(135deg, #6366f1, #818cf8)', 
    color: '#fff', 
    border: 'none', 
    padding: '12px', 
    borderRadius: 8, 
    fontWeight: 600, 
    cursor: 'pointer',
    fontSize: 14,
    transition: 'opacity 0.2s',
  },
  secondaryBtn: { 
    background: 'rgba(99,102,241,0.1)', 
    color: C.primary, 
    border: '1px solid rgba(99,102,241,0.2)', 
    padding: '12px', 
    borderRadius: 8, 
    fontWeight: 600, 
    cursor: 'pointer',
    fontSize: 14,
  },
  logoutBtn: {
    background: 'transparent',
    color: '#f87171',
    border: '1px solid rgba(248,113,113,0.2)',
    padding: '10px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 'auto',
  },
  input: {
    background: 'rgba(2,5,16,0.8)',
    border: '1px solid rgba(99,102,241,0.2)',
    borderRadius: 8,
    padding: '10px 12px',
    color: '#fff',
    fontSize: 14,
    outline: 'none',
  },
  error: { color: '#f87171', fontSize: 12, marginTop: 8 },
  codeContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px 0',
    minHeight: 240,
    background: 'rgba(0,0,0,0.2)',
    borderRadius: 12,
  },
  qrWrapper: { padding: 16, background: '#fff', borderRadius: 12 },
  pairingCode: { display: 'flex', gap: 8 },
  codeChar: { 
    width: 32, 
    height: 44, 
    background: 'rgba(99,102,241,0.15)', 
    border: '1px solid rgba(99,102,241,0.3)', 
    borderRadius: 6, 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    fontSize: 20,
    fontWeight: 700,
    color: C.primary,
  },
  loader: { width: 30, height: 30, border: '3px solid rgba(99,102,241,0.2)', borderTopColor: C.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' },
  toggleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, padding: '12px 16px', background: 'rgba(99,102,241,0.05)', borderRadius: 10, border: '1px solid rgba(99,102,241,0.1)' },
  statusBadge: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: C.success, letterSpacing: 0.5 },
  toggle: {
    position: 'relative',
    display: 'inline-block',
    width: 44,
    height: 24,
    cursor: 'pointer',
  },
  toggleInput: {
    opacity: 0,
    width: 0,
    height: 0,
  },
  toggleDot: {
    position: 'absolute',
    cursor: 'pointer',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.1)',
    transition: '0.3s',
    borderRadius: 24,
    display: 'flex',
    alignItems: 'center',
    padding: '0 4px',
    '&:before': {
      content: '""',
      position: 'absolute',
      height: 18,
      width: 18,
      left: 4,
      bottom: 3,
      backgroundColor: '#fff',
      transition: '0.3s',
      borderRadius: '50%',
    }
  }
};
